import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import {
  FormProvider,
  type SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box } from '~/components/elements/Box'
import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { VStack } from '~/components/elements/VStack'
import { ensureExists, mapItemsToObj } from '~/utils'

import { dialogInfoAtom, linkAnswersInfoAtom } from '../../atoms'

import { OptionInput } from './OptionInput'
import { type InputValues, resolver } from './utils'

export type LinkAnswersDialogProps = {
  onExecute: (index: number, options: InputValues['options']) => void
}

export const LinkAnswersDialog = ({ onExecute }: LinkAnswersDialogProps) => {
  const { t } = useTranslation()
  const linkAnswersInfo = useAtomValue(linkAnswersInfoAtom)
  const { index, question } = ensureExists(
    linkAnswersInfo,
    t('assertion_failed'),
  )
  const methods = useForm<InputValues>({
    defaultValues: { options: question.options },
    resolver,
  })
  const {
    control,
    formState: { isDirty },
    handleSubmit,
  } = methods
  const { append, fields, remove } = useFieldArray({
    control,
    keyName: 'fieldId',
    name: 'options',
  })

  const dialogInfo = useAtomValue(dialogInfoAtom)
  const surveyGroup = ensureExists(
    dialogInfo?.surveyGroup,
    t('assertion_failed'),
  )
  const handleAddButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    append({
      text: '',
      attachedOptionMap: mapItemsToObj(surveyGroup.surveys, () => null),
    })
  }, [append, surveyGroup.surveys])

  const resetLinkAnswersInfo = useResetAtom(linkAnswersInfoAtom)
  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ options }) => {
      if (!isDirty) {
        resetLinkAnswersInfo()

        return
      }

      onExecute(index, options)
      resetLinkAnswersInfo()
    },
    [index, isDirty, onExecute, resetLinkAnswersInfo],
  )

  return (
    <FormProvider {...methods}>
      <ExecuteCancelDialog
        executeButtonText={t('confirm_2')}
        mode="settings"
        title={t('link_questionnaire_items', { question })}
        width="100%"
        onCancel={resetLinkAnswersInfo}
        onExecute={handleSubmit(handleValid)}
      >
        <VStack spacing={0}>
          <Box padding={2}>
            <StyledButton icon="add" onClick={handleAddButtonClick}>
              {t('add_option')}
            </StyledButton>
          </Box>

          {fields.map((field, index) => (
            <OptionInput key={field.fieldId} index={index} onDelete={remove} />
          ))}
        </VStack>
      </ExecuteCancelDialog>
    </FormProvider>
  )
}
